import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  /** @type {boolean} Whether the current interaction started outside the dialog. */
  #pointerDownWasOutside = false;

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #pauseAndResetVideos() {
    const { dialog } = this.refs;
    const videos = dialog.querySelectorAll('video');

    videos?.forEach(video => {
      video.pause();
      video.currentTime = 0;
    });
  }

  #playVideos() {
    const { dialog } = this.refs;
    const videos = dialog.querySelectorAll('video');

    videos?.forEach((video) => {
      const lazySources = video.querySelectorAll('source[data-src]');
      if (lazySources.length > 0) {
        lazySources.forEach((source) => {
          const dataSrc = source.getAttribute('data-src');
          if (dataSrc) {
            source.setAttribute('src', dataSrc);
            source.removeAttribute('data-src');
          }
        });
        video.load();
      }

      const tryPlay = () => {
        const playPromise = video.play?.();
        if (typeof playPromise?.catch === 'function') {
          playPromise.catch(() => {});
        }
      };

      if (video.readyState >= 2) {
        tryPlay();
      } else {
        video.addEventListener('canplay', tryPlay, { once: true });
        video.addEventListener('loadedmetadata', tryPlay, { once: true });
      }
    });
  }
  /**
   * @returns {boolean}
   */
  #hasNestedOpenDialog() {
    const { dialog } = this.refs;
    return Boolean(dialog.querySelector('dialog[open]'));
  }

  #lockBodyScroll() {
    if (document.body.style.position === 'fixed') return;

    document.body.style.width = '100%';
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
  }

  #unlockBodyScroll() {
    if (document.querySelector('dialog[open]')) return;

    document.body.style.width = '';
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: parseInt(scrollY) * -1, behavior: 'instant' });
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const hadOpenDialog = Boolean(document.querySelector('dialog[open]'));

    if (!hadOpenDialog) {
      this.#lockBodyScroll();
    }

    dialog.showModal();
    this.dispatchEvent(new DialogOpenEvent());

    this.#playVideos();

    // Wait until the next tick to add the event listeners to avoid race condition
    // when `showDialog` is called within a click event listener.
    setTimeout(() => {
      this.addEventListener('pointerdown', this.#handlePointerDown);
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    this.#pauseAndResetVideos();

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());

    this.#unlockBodyScroll();
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (this.#hasNestedOpenDialog()) return;

    // A click with no detail is not a real pointer click: keyboard activation,
    // autofill and the mobile keyboard's done key all report clientX/clientY as
    // 0,0, which isClickedOutside reads as a point outside the dialog.
    if (event.detail === 0) return;

    // The click fires on the common ancestor of pointerdown and pointerup, so
    // selecting text inside a field and releasing past the dialog edge used to
    // dismiss it. Only treat it as an outside click when it also started outside.
    if (!this.#pointerDownWasOutside) return;

    if (isClickedOutside(event, dialog)) {
      event.stopPropagation();
      void this.closeDialog();
    }
  }

  /**
   * Records whether the interaction started outside the dialog, so a drag that
   * ends outside it does not count as an outside click.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  #handlePointerDown(event) {
    const { dialog } = this.refs;

    this.#pointerDownWasOutside = isClickedOutside(event, dialog);
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    if (this.#hasNestedOpenDialog()) return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
